import { Suspense, useState, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { AlertProvider } from "./contexts/AlertContext";
import { getBioContent, getHomeConfig } from "./firebase/firebase-functions";

// Non-lazy loaded pages
import Home from "./pages/Home";
import Bio from "./pages/Bio";
import Music from "./pages/Music";
import Contact from "./pages/Contact";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import NotFound from "./pages/NotFound";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";

// Components
import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import PageLoader from "./components/PageLoader";
import ProtectedRoute from "./components/ProtectedRoute";
import Alert from "./components/Alert";

const FALLBACK_BIO = {
  bioPage:
    "I'm a singer/songwriter with a passport full of stories. Born in Texas, raised in Nigeria, shaped by London, and now chasing my music dreams in New York City. Growing up all over the world, I discovered the power of music as a constant source of comfort and connection, a way of feeling at home no matter where I was.\n\nMy sound blends pop, R&B, and folk-inspired country melodies, rooted in everything I've seen, felt, and lived. I'm just getting started, and I'm so excited to share the journey with you.",
  homePageBioOne:
    "Born in Texas, raised in Nigeria, shaped by London, and now chasing music dreams in New York City. A 23-year-old artist with a passport full of stories and a sound that blends pop, R&B, and folk-inspired melodies.",
  homePageBioTwo:
    "My sound blends pop, R&B, and folk-inspired country melodies, rooted in everything I've seen, felt, and lived. I'm just getting started, and I'm so excited to share the journey with you.",
};

function App() {
  // null = still loading, object = resolved (Firebase or fallback)
  // Both fetched ONCE here — survive all page navigation

  const [bio, setBio] = useState(null);
  const [homeConfig, setHomeConfig] = useState(null);

  useEffect(() => {
    const fetchBio = async () => {
      const result = await getBioContent();
      if (result.success && result.bio) {
        setBio({
          bioPage: result.bio.bioPage || FALLBACK_BIO.bioPage,
          homePageBioOne:
            result.bio.homePageBioOne || FALLBACK_BIO.homePageBioOne,
          homePageBioTwo:
            result.bio.homePageBioTwo || FALLBACK_BIO.homePageBioTwo,
        });
      } else {
        setBio(FALLBACK_BIO);
      }
    };

    const fetchHomeConfig = async () => {
      const result = await getHomeConfig();
      // getHomeConfig always returns a config (falls back to defaults on error)
      setHomeConfig(result.config);
    };

    fetchBio();
    fetchHomeConfig();
  }, []);

  return (
    <AlertProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Header />
          <main>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Main pages — bio and homeConfig passed as props, no re-fetch on navigation */}
                <Route
                  path="/"
                  element={<Home bio={bio} homeConfig={homeConfig} />}
                />
                <Route path="/bio" element={<Bio bio={bio} />} />
                <Route path="/music" element={<Music />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />

                {/* Auth pages */}
                <Route path="/login" element={<Login />} />

                {/* Protected Dashboard — receives homeConfig so saves update the live site instantly */}
                <Route
                  path="/daswani-dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard
                        homeConfig={homeConfig}
                        onHomeConfigSaved={setHomeConfig}
                      />
                    </ProtectedRoute>
                  }
                />

                {/* 404 */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </main>
          <Footer />

          {/* Global Alert System */}
          <Alert />
        </BrowserRouter>
      </AuthProvider>
    </AlertProvider>
  );
}

export default App;
